import React, { useEffect, useState } from 'react';
import { IEntityInfoView } from '@components/widgets/entity-info/EntityInfoFields';
import EntityInfoWidget from '@components/widgets/entity-info/EntityInfoWidget';
import { currencyHeaders, entityView, steps } from './content';
import StaticLayout from '@components/layout/StaticLayout';
import { IListingData } from '@models/listing';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  createCurrencyExchangeRate,
  getLatestCurrencyExchangeRateList,
} from './queries';
import { isEmpty } from 'lodash';
import { toast } from 'react-toastify';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { getError } from '@utils/graph-utils';
import { OpenConfirmationAction } from '@redux/confirmation/actions';
import { useAppDispatch } from '@redux/hooks';
import { mapToCurrencyListingData } from './utils';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import EnhancedStepperSpecificDesign from '@components/stepper/EnhancedStepperSpecificDesign';
import { capitalizeFirstLetter } from '@utils/formatting-utils';
import { useNavigate } from 'react-router-dom';
import DateService from '@services/DateService';
import { ActionTarget, IEnhancedMenuItem } from '@components/table/types';
import ListingTable from '@components/table/ListingTable';
import { IAbstractRecord } from '@models';
import Loader from '@components/loader/Loader';
import { MAIN_THEME } from '@styles';

const CurrencyExchangeRateNewPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [updatedLeftSideInfo, setUpdatedLeftSideInfo] =
    useState<IEntityInfoView>(entityView);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [currencyTableData, setCurrencyTableData] = useState<IListingData>({
    pagedItems: {},
    pageSize: 100,
    pageNumber: 1,
    totalCount: 0,
  });

  const [currencyListQuery, { loading }] = useLazyQuery(
    getLatestCurrencyExchangeRateList()
  );

  const [createDailyExchangeRateResultsAction] = useMutation(
    createCurrencyExchangeRate(),
    {
      errorPolicy: 'all',
      refetchQueries: [getLatestCurrencyExchangeRateList()],
    }
  );

  const loadCurrencyList = async () => {
    const result = await currencyListQuery({
      variables: {
        currentPage: 1,
        currentPageSize: 1000,
      },
      errorPolicy: 'all',
    });

    if (result.data) {
      const currenciesTableData = mapToCurrencyListingData(result?.data);

      return currenciesTableData;
    }

    return null;
  };

  const initialize = async () => {
    const applicationProperties = entityView.sections[0];

    applicationProperties.properties.effectiveDate.value =
      DateService.formatDate(new Date());

    const newLeftEntityView = {
      ...entityView,
      sections: [applicationProperties],
    };
    const currencyList = await loadCurrencyList();
    Object.values(currencyList.pagedItems).forEach((row: IAbstractRecord) => {
      row.disabled = row.core_CurrencyExchangeRate_EffectiveFrom
        ? DateService.isSameDate(
            row.core_CurrencyExchangeRate_EffectiveFrom,
            new Date(),
            'day'
          )
        : false;
      row.Currency = row.core_CurrencyExchangeRate_CurrencyID_Symbol;
    });

    setUpdatedLeftSideInfo(newLeftEntityView);
    setCurrencyTableData(currencyList);
  };

  useEffect(() => {
    initialize();
  }, []);

  const currencyListingActions: IEnhancedMenuItem[] = [
    {
      name: 'save',
      title: 'Save',
      target: ActionTarget.Entity,
      rowIndependant: true,
      onClick: () => {
        const confirmation = {
          open: true,
          title: 'Save',
          message: (
            <>
              {'Are you sure you want to save your changes?'}
              <br />
              <i>
                <u>
                  {'Note that you can save the daily rate only once per day.'}
                </u>
              </i>
            </>
          ),
          callback: async () => {
            await handleSaveCallback();
          },
          submitButtonText: 'Submit',
          cancelButtonText: 'Cancel',
        };
        dispatch(OpenConfirmationAction(confirmation));
      },
    },
  ];

  const handleSaveCallback = async () => {
    setSubmitting(true);
    try {
      const modifiedRowData = Object.values(currencyTableData.pagedItems).map(
        (row: {
          core_CurrencyExchangeRate_CurrencyID_Code: string;
          core_CurrencyExchangeRate_Rate: string;
        }) => ({
          core_CurrencyExchangeRate_CurrencyID_Code:
            row.core_CurrencyExchangeRate_CurrencyID_Code,
          core_CurrencyExchangeRate_Rate: row.core_CurrencyExchangeRate_Rate,
        })
      );

      const variables = {
        rateImputs: modifiedRowData.map(
          (row: {
            core_CurrencyExchangeRate_CurrencyID_Code: string;
            core_CurrencyExchangeRate_Rate: string | number;
          }) => {
            return {
              currencyCode: row.core_CurrencyExchangeRate_CurrencyID_Code,
              rate:
                row.core_CurrencyExchangeRate_Rate === 0
                  ? 0
                  : row.core_CurrencyExchangeRate_Rate === '' ||
                      row.core_CurrencyExchangeRate_Rate === null ||
                      row.core_CurrencyExchangeRate_Rate === undefined
                    ? null
                    : row.core_CurrencyExchangeRate_Rate,
            };
          }
        ),
      };

      const result = await createDailyExchangeRateResultsAction({
        variables: variables,
        errorPolicy: 'all',
      });

      if (isEmpty(result.errors)) {
        toast.success(
          <ToastSuccessMessage>
            {'Currency Exchange Rate successfully updated.'}
          </ToastSuccessMessage>
        );

        setTimeout(() => {
          navigate(`/configuration/currency-exchange-rate/`);
        }, 500);
      } else {
        toast.error(<ToastErrorMessage>{getError(result)}</ToastErrorMessage>);
      }
    } catch (error) {
      toast.error(
        <ToastErrorMessage>{`An error occurred: ${error.message}`}</ToastErrorMessage>
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCurrencyCellValueChanged = (
    rowIndex: number,
    columnId: string,
    newValue: any
  ) => {
    setCurrencyTableData((prevData) => {
      const currentValue = prevData.pagedItems[rowIndex]?.[columnId];
      let parsedValue =
        typeof newValue === 'string' ? parseFloat(newValue) : newValue;

      if (isNaN(parsedValue)) {
        parsedValue = null;
      }

      if (currentValue === parsedValue || newValue === '') {
        return prevData;
      }

      const updatedPagedItems = {
        ...prevData.pagedItems,
        [rowIndex]: {
          ...prevData.pagedItems[rowIndex],
          [columnId]: parsedValue,
        },
      };

      return {
        ...prevData,
        pagedItems: updatedPagedItems,
      };
    });
  };

  const renderMainChildren = () => {
    return (
      <>
        <div style={{ marginTop: '20px' }}>
          <EnhancedStepperSpecificDesign
            activeStep={''}
            showStepperButton={false}
            style={{ visibility: 'hidden' }}
            steps={steps}
          />

          <WidgetSection style={{ margin: '-23px 0 0 0' }}>
            <form>
              <div
                style={{
                  marginTop: '20px',
                  padding: '-23px 0 0 0 !important',
                }}
              >
                {loading ? (
                  <Loader />
                ) : (
                  <ListingTable
                    inlineTitle={capitalizeFirstLetter(
                      'Currency Exchange Rate'
                    )}
                    name="currencyexchangerate"
                    orderByAscendingByDefault
                    defaultOrderByColumn="core_CurrencyExchangeRate_CurrencyID_Code"
                    inline
                    loader={loading}
                    data={currencyTableData}
                    headers={currencyHeaders}
                    disableSelection
                    actions={currencyListingActions}
                    handleCellValueChanged={handleCurrencyCellValueChanged}
                    pageSizeOptions={[5, 10, 25, 100]}
                  />
                )}
              </div>
            </form>
          </WidgetSection>
        </div>
      </>
    );
  };

  const renderLeftSection = () => {
    return (
      <>
        <EntityInfoWidget
          background={MAIN_THEME.color.red}
          classification={{ classifications: {}, value: '' }}
          progress={100}
          supportsClassification={true}
          supportsProgressBar={true}
          title={'Currency Exchange Rate'}
          view={updatedLeftSideInfo}
        ></EntityInfoWidget>
      </>
    );
  };

  return (
    <StaticLayout
      name={'Currency Exchange Rate'}
      leftChildren={renderLeftSection()}
      mainChildren={renderMainChildren()}
    />
  );
};

export default CurrencyExchangeRateNewPage;
